<template>
  <div
    class="read-more"
    :class="{
      'read-more--open': open,
      'read-more--closed': !open,
      'read-more--button-is-visible': !disabled
    }"
  >
    <div
      class="read-more__container"
      :style="style"
    >
      <div
        class="read-more__slot"
        ref="slotElement"
      >
        <slot />
      </div>
    </div>

    <div
      v-if="!disabled"
      class="read-more__btn"
      @click="toggle()"
      ref="lessButton"
    >
      {{ label }}
    </div>
  </div>
</template>

<script setup lang="ts">
  import {ref, computed, onMounted, onUpdated, onBeforeUnmount, withDefaults, defineProps} from 'vue';
  import {t} from '../../../bps/utilities/js/translations/translations';
  import scrollToElement from 'scroll-to-element';

  type Props = {
    maxHeight?: number;
    offset?: number;
    textMore?: string;
    textLess?: string;
    scrollLessElement?: string;
  };

  const props = withDefaults(defineProps<Props>(), {
    maxHeight: 100,
    offset: 80,
    textMore: 'bps.components.read_more.read_more',
    textLess: 'bps.components.read_more.read_less',
    scrollLessElement: ''
  });

  const open = ref(false);
  const elementHeight = ref(0);

  const slotElement = ref<HTMLElement | null>(null);
  const lessButton = ref<HTMLElement | null>(null);

  const disabled = computed(() => {
    return (props.maxHeight ?? 0) >= elementHeight.value || (elementHeight.value - (props.maxHeight ?? 0)) < props.offset;
  });

  const style = computed(() => {
    if (disabled.value) return null;
    return {
      maxHeight: open.value ? 'none' : `${props.maxHeight}px`
    };
  });

  const label = computed(() => {
    const key = open.value ? props.textLess : props.textMore;
    return t(key ?? '');
  });

  function setHeight() {
    const el = slotElement.value;
    if (el) {
      elementHeight.value = el.getBoundingClientRect().height + 30;
    }
  }

  function isElementInViewport(el: HTMLElement | null) {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    return rect && Math.round(rect.bottom) < 0;
  }

  function toggle() {
    if (disabled.value) return;

    open.value = !open.value;

    if (!open.value && props.scrollLessElement) {
      const item = document.querySelector(props.scrollLessElement);
      const stickyNavigation = document.querySelector('.product-navigation__container');
      let offsetHeight = -10;
      if (stickyNavigation && stickyNavigation.classList.contains('product-navigation__container--sticky')) {
        offsetHeight -= (stickyNavigation as HTMLElement).getBoundingClientRect().height;
      }

      window.setTimeout(() => {
        const lessBtn = lessButton.value;
        if (isElementInViewport(lessBtn)) {
          scrollToElement(item as any, {duration: 400, offset: offsetHeight});
        }
      }, 50);
    }
  }

  onMounted(() => {
    setHeight();
    window.addEventListener('resize', setHeight);
  });

  onUpdated(() => {
    setHeight();
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', setHeight);
  });
</script>
